import { useState, useEffect } from "react";
import { Plus, Trash2, ArrowRight, ArrowLeft, Calendar, AlertTriangle } from "lucide-react";
import "./Tarefas.css";

export default function Tarefas() {
    // --- ESTADO ---
    const [tasks, setTasks] = useState(() => {
        const saved = localStorage.getItem("myWorkspaceTasksKanban");
        if (!saved) return [];
        try {
            const parsed = JSON.parse(saved);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return [];
        }
    });

    const [newTask, setNewTask] = useState("");
    const [newDate, setNewDate] = useState("");
    const [showForm, setShowForm] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [newDescription, setNewDescription] = useState("");

    useEffect(() => {
        localStorage.setItem("myWorkspaceTasksKanban", JSON.stringify(tasks));
    }, [tasks]);

    // --- HANDLERS ---
    const handleAddTask = (e) => {
        e.preventDefault();
        if (!newTask.trim()) return;

        const taskItem = {
            id: Date.now(),
            title: newTask,
            date: newDate,
            description: newDescription, // <-- campo novo adicionado
            status: "todo", // ou a sua coluna inicial padrão
        };

        setTasks([...tasks, taskItem]);
        setNewTask("");
        setNewDate("");
        setNewDescription("");
        setShowForm(false);
    };

    const toggleForm = () => {
        if (showForm) {
            setNewTask("");
            setNewDate("");
        }
        setShowForm(!showForm);
    };

    const requestDelete = (id) => setItemToDelete(id);

    const confirmDelete = () => {
        if (itemToDelete) {
            setTasks(tasks.filter(t => t.id !== itemToDelete));
            setItemToDelete(null);
        }
    };

    const moveTask = (id, direction) => {
        const statusOrder = ["todo", "doing", "done"];
        setTasks(tasks.map(t => {
            if (t.id !== id) return t;

            const currentIndex = statusOrder.indexOf(t.status);
            if (currentIndex === -1) return { ...t, status: "todo" };

            let newIndex = currentIndex;
            if (direction === "next" && currentIndex < statusOrder.length - 1) newIndex++;
            if (direction === "prev" && currentIndex > 0) newIndex--;
            return { ...t, status: statusOrder[newIndex] };
        }));
    };

    // --- ESTATÍSTICAS ---
    const totalTasks = tasks.length;
    const doneTasks = tasks.filter(t => t.status === "done").length;
    const progress = totalTasks === 0 ? 0 : Math.round((doneTasks / totalTasks) * 100);

    // --- DEADLINE ---
    const getDeadlineText = (dateString) => {
        if (!dateString) return null;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const due = new Date(dateString + "T00:00:00");
        due.setHours(0, 0, 0, 0);

        const diffDays = Math.round((due - today) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) return "Atrasada";
        if (diffDays === 0) return "Faça hoje!";
        if (diffDays === 1) return "Falta 1 dia";
        return `Faltam ${diffDays} dias`;
    };

    // --- COLUNAS ---
    const columns = [
        { id: "todo", title: "Não Iniciadas", color: "gray" },
        { id: "doing", title: "Em curso", color: "blue" },
        { id: "done", title: "Concluída", color: "green" }
    ];

    return (
        <div className="tarefas-kanban-container">

            {/* HEADER */}
            <header className="kanban-header">
                <div>
                    <h1>Tarefas</h1>
                    <div className="header-tabs">
                        <span className="active">Status</span>
                        <span style={{ opacity: 0.5, cursor: "not-allowed" }}>Hoje</span>
                        <span style={{ opacity: 0.5, cursor: "not-allowed" }}>Calendário</span>
                    </div>
                </div>
                <button className="btn-new-task-kanban" onClick={toggleForm}>
                    <Plus size={18} /> {showForm ? "Cancelar" : "Criar nova tarefa"}
                </button>
            </header>

            {/* MODAL DE NOVA TAREFA */}
            {showForm && (
                <div className="task-modal-overlay" onClick={() => setShowForm(false)}>
                    <div className="task-modal-card" onClick={e => e.stopPropagation()}>
                        <div className="task-modal-title">
                            <h2>Criar Nova Tarefa</h2>
                            <p>Preencha os dados e etapas da sua atividade</p>
                        </div>

                        <form onSubmit={handleAddTask} className="task-modal-body">
                            <div className="task-input-group">
                                <label>Título da Tarefa</label>
                                <input
                                    autoFocus
                                    type="text"
                                    placeholder="O que precisa ser feito?"
                                    value={newTask}
                                    onChange={e => setNewTask(e.target.value)}
                                    required
                                />
                            </div>

                            <div className="task-input-group">
                                <label>Data de Conclusão / Prazo</label>
                                <input
                                    type="date"
                                    value={newDate}
                                    onChange={e => setNewDate(e.target.value)}
                                />
                            </div>

                            <div className="task-input-group">
                                <label>Descrição</label>
                                <textarea
                                    rows="4"
                                    placeholder="Adicione notas, etapas ou observações..."
                                    value={newDescription}
                                    onChange={e => setNewDescription(e.target.value)}
                                />
                            </div>

                            <div className="task-modal-footer">
                                <button
                                    type="button"
                                    className="btn-task-cancel"
                                    onClick={() => setShowForm(false)}
                                >
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-task-submit">
                                    Adicionar Tarefa
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="kanban-layout">

                {/* --- QUADRO KANBAN --- */}
                <div className="kanban-board">
                    {columns.map(col => (
                        <div key={col.id} className="kanban-column">
                            <div className="column-header-kanban">
                                <span className={`status-dot ${col.color}`}></span>
                                <span className="col-title">{col.title}</span>
                                <span className="col-count">
                                    {tasks.filter(t => t.status === col.id).length}
                                </span>
                            </div>

                            <div className="column-content">
                                {tasks
                                    .filter(t => t.status === col.id)
                                    .map(task => (
                                        <div key={task.id} className="kanban-card">
                                            <div className="card-top">
                                                <span className="card-title-text">{task.title}</span>
                                                <button
                                                    onClick={() => requestDelete(task.id)}
                                                    className="btn-del-mini"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>

                                            {task.date && (
                                                <div
                                                    className={`card-deadline ${getDeadlineText(task.date) === "Atrasada" ? "late" : ""
                                                        }`}
                                                >
                                                    <Calendar size={12} />
                                                    {getDeadlineText(task.date)}
                                                </div>
                                            )}

                                            <div className="card-controls">
                                                <button
                                                    onClick={() => moveTask(task.id, "prev")}
                                                    className={`btn-move ${col.id === "todo" ? "hidden" : ""}`}
                                                    aria-label="Mover para coluna anterior"
                                                >
                                                    <ArrowLeft size={14} />
                                                </button>

                                                <button
                                                    onClick={() => moveTask(task.id, "next")}
                                                    className={`btn-move ${col.id === "done" ? "hidden" : ""}`}
                                                    aria-label="Mover para próxima coluna"
                                                >
                                                    <ArrowRight size={14} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    ))}
                </div>

                {/* --- PAINEL LATERAL (CONCLUSÃO) --- */}
                <div className="kanban-sidebar">
                    <div className="sidebar-box">
                        <h3>📁 Conclusão</h3>
                        <div className="stats-row">
                            <span>Progresso Geral</span>
                            <span className="percent">{progress}%</span>
                        </div>
                        <div className="progress-bar-bg">
                            <div
                                className="progress-bar-fill"
                                style={{ width: `${progress}%` }}
                            ></div>
                        </div>

                        <div className="mini-stats-list">
                            <div className="mini-stat">
                                <span className="label">Total</span>
                                <span className="value">{totalTasks}</span>
                            </div>
                            <div className="mini-stat">
                                <span className="label">Feitas</span>
                                <span className="value success">{doneTasks}</span>
                            </div>
                            <div className="mini-stat">
                                <span className="label">A Fazer</span>
                                <span className="value warning">{totalTasks - doneTasks}</span>
                            </div>
                        </div>
                    </div>
                </div>

            </div>

            {/* --- MODAL DE CONFIRMAÇÃO (escopo tarefas-) --- */}
            {itemToDelete && (
                <div
                    className="tarefas-modal-overlay"
                    onClick={() => setItemToDelete(null)}
                >
                    <div
                        className="tarefas-modal-box"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="tarefas-modal-icon">
                            <AlertTriangle size={48} />
                        </div>
                        <h3>Excluir Tarefa?</h3>
                        <p>Tem certeza que deseja excluir esta tarefa?</p>
                        <div className="tarefas-modal-actions">
                            <button
                                className="tarefas-btn-cancel"
                                onClick={() => setItemToDelete(null)}
                            >
                                Cancelar
                            </button>
                            <button
                                className="tarefas-btn-confirm"
                                onClick={confirmDelete}
                            >
                                Sim, Excluir
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}